import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  ActivityType,
  CategoryType,
  DefaultExpenseSource,
  EntityType,
  EventType,
  ExpenseSourceType,
  GqlService,
  UpdateAccrualsInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Utils } from '@shared/utils/utils';

import {
  createInitialState,
  ExpenseDefaultsStore,
  ExpenseSourceItems,
} from './expense-defaults.store';

@Injectable({ providedIn: 'root' })
export class ExpenseDefaultsService {
  private readonly eventService = inject(EventService);
  private readonly expenseDefaultsStore = inject(ExpenseDefaultsStore);
  private readonly gqlService = inject(GqlService);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);

  mapExpenseCategory = new Map<CategoryType, string>([
    [CategoryType.CATEGORY_INVESTIGATOR, 'Investigator'],
    [CategoryType.CATEGORY_PASSTHROUGH, 'Pass-through'],
    [CategoryType.CATEGORY_SERVICE, 'Services'],
  ]);
  mapExpenseType = new Map<ExpenseSourceType, string>([
    [ExpenseSourceType.EXPENSE_SOURCE_EVIDENCE_BASED, 'Evidence Based'],
    [ExpenseSourceType.EXPENSE_SOURCE_FORECAST, 'Forecast'],
    [ExpenseSourceType.EXPENSE_SOURCE_INVOICE, 'Invoice'],
    [ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE, 'Vendor Estimate'],
  ]);

  getExpenseDefaults() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.expenseDefaultsStore.setLoading(true);
        this.expenseDefaultsStore.update(createInitialState());

        return this.gqlService.listDefaultExpenseSources$().pipe(
          tap(({ data, success }) => {
            if (success && data) {
              const nData = data.map((x) => {
                return {
                  ...x,
                  cat: this.mapExpenseCategory.get(x.cost_category) || '',
                  primary: this.mapExpenseType.get(x?.sources?.[0] as ExpenseSourceType) || '',
                  quaternary:
                    this.mapExpenseType.get(x?.sources?.[3] as ExpenseSourceType) ||
                    Utils.zeroHyphen,
                  secondary: this.mapExpenseType.get(x?.sources?.[1] as ExpenseSourceType) || '',
                  tertiary:
                    this.mapExpenseType.get(x?.sources?.[2] as ExpenseSourceType) ||
                    Utils.zeroHyphen,
                } as ExpenseSourceItems;
              });

              this.expenseDefaultsStore.update({
                items: nData || [],
              });
            }

            this.expenseDefaultsStore.setLoading(false);
          })
        );
      })
    );
  }

  async updateExpenseDefaults(expenseSources: DefaultExpenseSource[]) {
    this.expenseDefaultsStore.setLoading(true);
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.batchUpdateDefaultExpenseSources$(expenseSources)
    );

    if (errors.length) {
      this.overlayService.error(errors);

      return false;
    }

    if (success) {
      const uData = this.expenseDefaultsStore.getValue().items.map((x) => {
        const isChange = data?.find(
          (y) => y.organization_id === x.organization_id && y.cost_category === x.cost_category
        );

        if (isChange) {
          const [primary, secondary, tertiary, quaternary] = isChange.sources;

          return {
            cat: this.mapExpenseCategory.get(x.cost_category) || '',
            cost_category: isChange.cost_category,
            organization_id: isChange.organization_id,
            primary: this.mapExpenseType.get(primary),
            quaternary: this.mapExpenseType.get(quaternary) || '',
            secondary: this.mapExpenseType.get(secondary),
            sources: isChange.sources,
            tertiary: this.mapExpenseType.get(tertiary) || '',
          } as ExpenseSourceItems;
        }

        return x;
      });

      this.expenseDefaultsStore.update({ items: uData });

      const organizationIds = [...new Set(expenseSources.map((source) => source.organization_id))];

      const updateAccrualsInput: UpdateAccrualsInput = {
        accrual_inputs: organizationIds.map((organization_id) => ({
          activity_types: expenseSources
            .filter((expenseSource) => expenseSource.organization_id === organization_id)
            .map((expenseSource) =>
              expenseSource.cost_category.replace('CATEGORY', 'ACTIVITY')
            ) as ActivityType[],
          organization_id,
        })),
      };

      await firstValueFrom(
        this.eventService.processEvent$({
          entity_id: '',
          entity_type: EntityType.TRIAL,
          payload: JSON.stringify(updateAccrualsInput),
          type: EventType.UPDATE_QUARTER_ACCRUALS,
        })
      );
    }

    this.expenseDefaultsStore.setLoading(false);

    return true;
  }
}
